import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircleQuestion, History } from "lucide-react";

const MAX = 20;
const normalize = (v) => (v || "").replace(/\s+/g, " ").trim();

// Industry-card section: the help assistant name for THIS tenant's industry ("Ask <name>"). One shared value per
// industry (all tenants of the industry see it); Super Admin only; audited; blank / >20 chars rejected; preview before save.
export function SaHelperName({ profile, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);
  useEffect(() => { setVal(profile?.helper_name || ""); setEdit(false); setHistory(null); }, [profile?.key, profile?.helper_name]);
  if (!profile) return null;
  const norm = normalize(val), blank = !norm, tooLong = norm.length > MAX, dirty = norm !== profile.helper_name;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/superadmin/industries/${profile.key}/helper-name`, { helper_name: val });
      toast.success(`${profile.name}: staff now see "Ask ${data.helper_name}"`); setEdit(false); onSaved?.(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const loadHistory = () => api.get(`/superadmin/industries/${profile.key}/helper-name/history`).then((r) => setHistory(r.data.history)).catch(() => setHistory([]));
  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-2.5 space-y-1.5" data-testid="sa-helper-name-section">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1"><MessageCircleQuestion className="w-3.5 h-3.5" /> Help assistant · {profile.name}</p>
        <span className="text-sm font-medium" data-testid="sa-helper-name-current">Ask {profile.helper_name}{profile.helper_name_custom ? "" : <span className="text-[10px] text-muted-foreground font-normal"> (default)</span>}</span>
      </div>
      <p className="text-[11px] text-muted-foreground">Shared by every tenant in this industry; tenant staff cannot change it. Industries without a configured name use Ask Eli.</p>
      {!edit && (
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setVal(profile.helper_name); setEdit(true); }} data-testid="sa-helper-name-edit">Change name</Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => (history ? setHistory(null) : loadHistory())} data-testid="sa-helper-name-history-btn"><History className="w-3.5 h-3.5 mr-1" /> {history ? "Hide history" : "History"}</Button>
        </div>
      )}
      {edit && (
        <div className="space-y-1.5" data-testid="sa-helper-name-form">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={profile.helper_name_default} className="h-8 text-sm" aria-label={`Help assistant name for ${profile.name}`} data-testid="sa-helper-name-input" />
          <p className="text-xs" data-testid="sa-helper-name-preview">Preview: <b>{blank ? "—" : `Ask ${norm}`}</b> <span className="text-muted-foreground">({norm.length}/{MAX})</span></p>
          {blank && <p className="text-[11px] text-destructive" data-testid="sa-helper-name-blank">The name cannot be blank.</p>}
          {tooLong && <p className="text-[11px] text-destructive" data-testid="sa-helper-name-too-long">Maximum {MAX} characters after trimming (you have {norm.length}).</p>}
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-xs" disabled={busy || blank || tooLong || !dirty} onClick={save} data-testid="sa-helper-name-save">Save</Button>
            {profile.helper_name_custom && <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy} onClick={() => setVal(profile.helper_name_default)} data-testid="sa-helper-name-use-default">Use default ({profile.helper_name_default})</Button>}
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEdit(false)} data-testid="sa-helper-name-cancel">Cancel</Button>
          </div>
        </div>
      )}
      {history && (
        <ul className="text-[11px] text-muted-foreground space-y-0.5 border-t border-border pt-1.5" data-testid="sa-helper-name-history">
          {history.length === 0 && <li>No changes recorded — using the default name.</li>}
          {history.map((h, i) => <li key={i}>{(h.at || "").slice(0, 16).replace("T", " ")} · {h.by}: {h.from} → <b>{h.to}</b></li>)}
        </ul>
      )}
    </div>
  );
}
